import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { Observable, throwError } from 'rxjs';
import { ODataResponse } from '../models/protocols/odata-response';
import { Endpoints } from './endpoints';
import { catchError, map } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { MemberSubmission, SubmissionStakeholder } from '../models/submissions/member-submission';
import { SharedSubmission } from '../models/submissions/shared-submission';
import { ChangeNameRequest } from '../models/users/requests/submissionRequests/change-name-request';
import { CreateAttachmentRequest } from '../models/users/requests/create-attachment-request';
import { SubmissionAttachment } from '../models/submissions/submission-attachment';
import { SubmissionCreated } from '../models/submissions/submission-created';
import { SubmissionCommentRequest } from '../models/users/requests/submission-comment-request';
import { SubmissionComment } from '../models/submissions/submission-comment';
import { SubmissionStatusCommentRequest } from '../models/submissions/requests/submission-comment-request';
import { HttpResponse } from '@angular/common/http';
import { SubmissionSubStatusEnum } from '../models/enum/shared/submission-sub-status.enum';
import { UpdateSubmissionOwnerRequest } from '../models/users/requests/update-submission-owner-request';
import { UpdateSubmissionApproverRequest } from '../models/users/requests/update-submission-approver-request';
import { SubmissionRoleTemplate } from '../models/submissions/submission-role-template';
import { MemberSubmissionType } from '../models/submissions/member-submission-type';
import { SubmissionPermissionType } from '../models/permissions/dto/submission-permission-type';
import { SubmissionPermissionWithType } from '../models/permissions/dto/submission-permission-with-type';
import { ChangeAddressRequest } from '../models/users/requests/submissionRequests/change-address-request';
import { SubmissionReminderRequest } from '../models/users/requests/submission-reminder-request';
import { UpdatePhoneNumberRequest } from '../models/users/requests/submissionRequests/update-phone-number-request';
import { ChangePersonalDetailsRequest } from '../models/users/requests/submissionRequests/change-personal-details-request';
import { IPChangeInvestmentInstructionsSubmissionRequest } from '../models/users/requests/ip-change-investment-instructions-submission-request';
import { MpChangeInvestmentInstructionSubmissionRequest } from '../models/users/requests/submissionRequests/mp-change-investment-instruction-submission-request';
import { RelationshipStatusSubmissionRequest } from '../models/submissions/requests/relationship-status-submission-request';
import { MpAccountBeneficiaryChangeSubmissionRequest } from '../models/submissions/requests/mp-account-beneficiary-change-submission-request';
import { IpAccountBeneficiaryChangeSubmissionRequest } from '../models/submissions/requests/ip-account-beneficiary-change-submission-request';
import { IpVBBeneficiaryChangeSubmissionRequest } from '../models/submissions/requests/ip-vb-beneficiary-change-submission-request';
import { CreateBankingInformationSubmissionRequest } from '../models/users/requests/submissionRequests/create-banking-information-submission-request';
import { CreateTransferInSubmissionRequest } from '../models/users/requests/submissionRequests/create-transfer-in-submission-request';
import { CreateLegalFiduciarySubmissionRequest } from '../models/users/requests/submissionRequests/create-legal-fiduciary-submission-request';
import { CreateVariableBenefitConfigurationSubmissionRequest } from '../models/users/requests/submissionRequests/create-variable-benefit-configuration-submission-request';
import { VbConfigurationEstimatedAnnualPaymentTotalRequest } from '../models/transactions/vb-configuration-estimated-annual-payment-total-request';
import { MemberVbAccountEstimatedAnnualPaymentTotal } from '../models/transactions/member-vb-account-estimated-annual-payment-total';
import { MpVbBeneficiaryChangeSubmissionRequest } from '../models/submissions/requests/mp-vb-beneficiary-change-submission-request';
import { UpdateVariableBenefitConfigurationRequest } from '../models/users/requests/submissionRequests/update-variable-benefit-configuration-request';
import { IpEmployerSubmission } from '../models/submissions/employer-submissions/ip-employer-submission';
import { UpdateSubmissionStatusRequest } from '../models/submissions/requests/update-submission-status-request';
import { EmployerSubmissionReminderRequest } from '../models/users/requests/employer-submission-reminder-request';
import { EmployerSubmissionAttachment } from '../models/submissions/employer-submissions/employer-submission-attachment';
import { EpEmployerSubmission } from '../models/submissions/employer-submissions/ep-employer-submission';
import { CreateVariableBenefitPaymentChangeRequest } from '../models/users/requests/submissionRequests/create-variable-benefit-payment-change-request';
import { MemberApprovedSubmission } from '../models/submissions/member-approved-submission';
import { MemberFundingBlockingSubmission } from '../models/submissions/member-funding-submission-in-progress';
import { MemberRetirementProductSubmissionAvailability } from '../models/submissions/member-retirement-product-submission-availability';
import { SubmissionTypeEnum } from '../models/users/enum/submission-type-enum';
import { SubmissionStatusIdEnum } from '../models/enum/shared/submission-status-id.enum';
import { MpRetirementProductSubmissionAvailability } from '../models/submissions/mp-retirement-product-submission-availability';
import { EmployerSubmissionTypeEnum } from '../models/enum/shared/employer-submission-type.enum';
import { AccountTypeIdEnum } from '../models/transactions/enum/account-type-id.enum';
import { CreateAnnuitySetupSubmissionRequest } from '../models/users/requests/submissionRequests/create-annuity-setup-submission-request';
import { EmployerMemberSubmission } from '../models/submissions/employer-submissions/employer-member-submission';
import { UpdateTransferInTransferRecordRequest } from '../models/users/requests/update-transfer-in-transfer-record-request';

@Injectable({
  providedIn: 'root'
})
export class SubmissionsAPI implements LoggableAPI {
  constructor() {}

  private apiClient = inject(ApiClient);

  public serviceName = 'Submissions';

  public ipGetMemberSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberSubmission>> {
    const url = Endpoints.ipGetMemberSubmissions();
    oDataQueryOptions.setExpand('Status', 'Substatus', 'Owner', 'Approver', 'Reminder');
    return this.apiClient.getOdata<MemberSubmission>(url, MemberSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetSharedSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<SharedSubmission>> {
    const url = Endpoints.ipGetSharedSubmissions();
    return this.apiClient.getOdata<SharedSubmission>(url, SharedSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissions(
    oDataQueryOptions: ODataQueryOptions,
    additionalExpand: string[] = []
  ): Observable<ODataResponse<IpEmployerSubmission>> {
    const url = Endpoints.ipGetEmployerSubmissions();
    oDataQueryOptions.setExpand('Status', 'SubStatus', 'Owner', 'Approver', 'Reminder', ...additionalExpand);
    return this.apiClient.getOdata<IpEmployerSubmission>(url, IpEmployerSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetSharedSubmissionOwners(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetSharedSubmissionOwners();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionOwners(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionOwners();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionOwners(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionOwners();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetSharedSubmissionApprovers(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetSharedSubmissionApprovers();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionApprovers(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionApprovers();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionApprovers(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionApprovers();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberSubmission>> {
    oDataQueryOptions.setExpand('Status');
    const url = Endpoints.getMemberSubmissions();
    return this.apiClient.getOdata<MemberSubmission>(url, MemberSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionById(id: string): Observable<MemberSubmission> {
    const url = Endpoints.ipGetMemberSubmissionById(id);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Status',
      'SubStatus',
      'Owner',
      'Approver',
      'Reminder',
      'Attachments($expand=Attachment)',
      'History',
      'Comments($expand=Author)',
      'NameChangeSubmission',
      'AddressChangeSubmission($expand=Previous($expand=State,Country)',
      'New($expand=State,Country))',
      'PhoneNumberSubmission',
      'PersonalDetailsSubmission',
      'InvestmentInstructionSubmission($expand=Previous($expand=InvestmentInstructions($expand=Fund), RebalancingInstructions($expand=Fund)), New($expand=InvestmentInstructions($expand=Fund), RebalancingInstructions($expand=Fund)))',
      'RelationshipStatusSubmission($expand=Previous($expand=RelationshipStatus, Spouse($expand=Address($expand=State, Country))), New($expand=RelationshipStatus, Spouse($expand=Address($expand=State, Country))))',
      'BeneficiaryChangeSubmission($expand=Previous($expand=Beneficiaries($expand=Type, RelationshipToMember, Address($expand=State,Country))),New($expand=Beneficiaries($expand=Type, RelationshipToMember, Address($expand=State,Country))))',
      'BankingInformationSubmission',
      'SelfEnrolmentSubmission($expand=New($expand=Gender, Pronoun, Address($expand=State, Country)))',
      'LegalFiduciarySubmission($expand=Previous($expand=Type, EntityType, PowerOfAttorneyType, PowerOfAttorneyAppointmentType, OrganizationEntity($expand=Address($expand=State, Country)), IndividualEntities($expand=Address($expand=State, Country))), New($expand=Type, EntityType, PowerOfAttorneyType, PowerOfAttorneyAppointmentType, OrganizationEntity($expand=Address($expand=State, Country)), IndividualEntities($expand=Address($expand=State, Country))))',
      'VariableBenefitConfigurationSubmission($expand=New($expand=BankAccount, RecurringPaymentInstructions($expand=PaymentFrequency), WithdrawalInstructions($expand=Fund), ConsentWaiver($expand=Attachment))), Owner',
      'UpdateVariableBenefitConfigurationSubmission($expand=New($expand=MemberConsentWaiver($expand=Attachment), SubmissionConsentWaiver($expand=Attachment)))',
      'VariableBenefitPaymentChangeSubmission($expand=Previous($expand=BankAccount, LumpSumPaymentInstruction, AnnualMinimumDetails, ActiveAnnualRecurringPaymentInstruction($expand=PaymentFrequency), ActiveMonthlyRecurringPaymentInstruction($expand=PaymentFrequency), ScheduledMonthlyRecurringPaymentInstruction($expand=PaymentFrequency), ScheduledAnnualRecurringPaymentInstruction($expand=PaymentFrequency), WithdrawalInstructions($expand=Fund)), New($expand=BankAccount, LumpSumPaymentInstruction, AnnualMinimumDetails, ActiveAnnualRecurringPaymentInstruction($expand=PaymentFrequency), ScheduledMonthlyRecurringPaymentInstruction($expand=PaymentFrequency), ActiveMonthlyRecurringPaymentInstruction($expand=PaymentFrequency), ScheduledAnnualRecurringPaymentInstruction($expand=PaymentFrequency), WithdrawalInstructions($expand=Fund)))',
      'AnnuitySetupSubmission($expand=New($expand=BankAccount, AnnuityType, ConsentWaiver($expand=Attachment)))',
      'TransferInSubmission($expand=New/InstitutionAddress)'
    );
    return this.apiClient.getOdataObj(url, MemberSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberSubmissionById(id: string): Observable<MemberSubmission> {
    const url = Endpoints.getMemberSubmissionById(id);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Status',
      'Attachments($expand=Attachment)',
      'Comments($expand=Author)',
      'NameChangeSubmission',
      'BeneficiaryChangeSubmission($expand=Previous($expand=Beneficiaries($expand=Type, RelationshipToMember, Address($expand=State,Country))),New($expand=Beneficiaries($expand=Type, RelationshipToMember, Address($expand=State,Country))))',
      'AddressChangeSubmission($expand=Previous($expand=State,Country),New($expand=State,Country))',
      'PhoneNumberSubmission',
      'PersonalDetailsSubmission',
      'InvestmentInstructionSubmission($expand=Previous($expand=InvestmentInstructions($expand=Fund), RebalancingInstructions($expand=Fund)), New($expand=InvestmentInstructions($expand=Fund), RebalancingInstructions($expand=Fund)))',
      'RelationshipStatusSubmission($expand=Previous($expand=RelationshipStatus, Spouse($expand=Address($expand=State, Country))), New($expand=RelationshipStatus, Spouse($expand=Address($expand=State, Country))))',
      'BankingInformationSubmission',
      'LegalFiduciarySubmission($expand=Previous($expand=Type, EntityType, PowerOfAttorneyType, PowerOfAttorneyAppointmentType, OrganizationEntity($expand=Address($expand=State, Country)), IndividualEntities($expand=Address($expand=State, Country))), New($expand=Type, EntityType, PowerOfAttorneyType, PowerOfAttorneyAppointmentType, OrganizationEntity($expand=Address($expand=State, Country)), IndividualEntities($expand=Address($expand=State, Country))))',
      'VariableBenefitConfigurationSubmission($expand=New($expand=BankAccount, RecurringPaymentInstructions($expand=PaymentFrequency), WithdrawalInstructions($expand=Fund), ConsentWaiver($expand=Attachment)))',
      'UpdateVariableBenefitConfigurationSubmission($expand=New($expand=MemberConsentWaiver($expand=Attachment), SubmissionConsentWaiver($expand=Attachment)))',
      'VariableBenefitPaymentChangeSubmission($expand=Previous($expand=BankAccount, LumpSumPaymentInstruction, AnnualMinimumDetails, ActiveAnnualRecurringPaymentInstruction($expand=PaymentFrequency), ActiveMonthlyRecurringPaymentInstruction($expand=PaymentFrequency), ScheduledMonthlyRecurringPaymentInstruction($expand=PaymentFrequency), ScheduledAnnualRecurringPaymentInstruction($expand=PaymentFrequency), WithdrawalInstructions($expand=Fund)), New($expand=BankAccount, LumpSumPaymentInstruction, AnnualMinimumDetails, ActiveAnnualRecurringPaymentInstruction($expand=PaymentFrequency), ScheduledMonthlyRecurringPaymentInstruction($expand=PaymentFrequency), ActiveMonthlyRecurringPaymentInstruction($expand=PaymentFrequency), ScheduledAnnualRecurringPaymentInstruction($expand=PaymentFrequency), WithdrawalInstructions($expand=Fund)))',
      'AnnuitySetupSubmission($expand=New($expand=BankAccount, AnnuityType, ConsentWaiver($expand=Attachment)))',
      'TransferInSubmission($expand=New/InstitutionAddress)'
    );
    return this.apiClient.getOdataObj(url, MemberSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionViewersForMemberSubmissionType(
    subTypeId: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionViewersForMemberSubmissionType();
    const params: string = `submissionTypeId=${subTypeId}`;
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, null, params)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public createInternalNameChangeSubmission(req: ChangeNameRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalNameChangeSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createNameChangeSubmission(req: ChangeNameRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createNameChangeSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalPersonalDetailsChangeSubmission(
    req: ChangePersonalDetailsRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalPersonalDetailsSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createPersonalDetailsChangeSubmission(req: ChangePersonalDetailsRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createPersonalDetailsSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalAddressChangeSubmission(req: ChangeAddressRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalAddressChangeSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createAddressChangeSubmission(req: ChangeAddressRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createAddressChangeSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalUpdatePhoneNumberSubmission(req: UpdatePhoneNumberRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalUpdatePhoneNumberSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createUpdatePhoneNumberSubmission(req: UpdatePhoneNumberRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createUpdatePhoneNumberSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createMemberInvestmentInstructionsSubmission(
    req: MpChangeInvestmentInstructionSubmissionRequest,
    accountTypeId: AccountTypeIdEnum
  ): Observable<SubmissionCreated> {
    let url = '';
    if (accountTypeId === AccountTypeIdEnum.ContributionAccount) {
      url = Endpoints.createMemberContributionInvestmentInstructionsSubmission();
    } else if (accountTypeId === AccountTypeIdEnum.VariableBenefitAccount) {
      url = Endpoints.createMemberVariableBenefitInvestmentInstructionsSubmission();
    } else {
      const err = new CustomError(new Error(`Unsupported account type ID: ${accountTypeId}`), this.serviceName);
      return throwError(() => err);
    }
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createIPMemberInvestmentInstructionsSubmission(
    req: IPChangeInvestmentInstructionsSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.createIPMemberInvestmentInstructionsSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateRelationshipStatusSubmission(req: RelationshipStatusSubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateRelationshipStatusSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateRelationshipStatusSubmission(req: RelationshipStatusSubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateRelationshipStatusSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateContributionAccountBeneficiaryUpdateSubmission(
    req: MpAccountBeneficiaryChangeSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateContributionAccountBeneficiaryUpdateSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateVBAccountBeneficiaryUpdateSubmission(
    req: MpVbBeneficiaryChangeSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateVBAccountBeneficiaryUpdateSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateAnnuityBeneficiaryUpdateSubmission(
    req: MpAccountBeneficiaryChangeSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateAnnuityAccountBeneficiaryUpdateSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateContributionAccountBeneficiaryUpdateSubmission(
    req: IpAccountBeneficiaryChangeSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateContributionAccountBeneficiaryUpdateSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateVBAccountBeneficiaryUpdateSubmission(
    req: IpVBBeneficiaryChangeSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateVBAccountBeneficiaryUpdateSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateAnnuityAccountBeneficiaryUpdateSubmission(
    req: IpAccountBeneficiaryChangeSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateAnnuityAccountBeneficiaryUpdateSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateBankingInformationSubmission(
    req: CreateBankingInformationSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateBankingInformationSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateBankingInformationSubmission(
    req: CreateBankingInformationSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateBankingInformationSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateTransferInSubmission(req: CreateTransferInSubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateTransferInSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateTransferInSubmission(req: CreateTransferInSubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateTransferInSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateLegalFiduciarySubmission(req: CreateLegalFiduciarySubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateLegalFiduciarySubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberActiveVariableBenefitConfigSubmission(
    memberId: number,
    opts = new ODataQueryOptions()
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipGetMemberActiveVariableBenefitConfigSubmission(memberId);
    return this.apiClient.getOdataObj(url, MemberSubmission, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberFundingBlockingSubmission(memberId: number): Observable<MemberFundingBlockingSubmission> {
    const url = Endpoints.ipGetMemberFundingBlockingSubmission(memberId);
    return this.apiClient.getObj(MemberFundingBlockingSubmission, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberInProgressRelationshipStatusSubmissions(memberId: number): Observable<MemberSubmission[]> {
    const opts = new ODataQueryOptions();
    opts.setFilter(
      `memberId eq ${memberId} and ` +
        `submissionTypeId eq ${SubmissionTypeEnum.RelationshipStatusChange} and ` +
        `(statusId eq ${SubmissionStatusIdEnum.Submitted} or statusId eq ${SubmissionStatusIdEnum.InReview})`
    );
    return this.ipGetMemberSubmissions(opts).pipe(map(response => response.value ?? []));
  }

  public ipGetMemberRetirementProductSubmissionAvailability(
    memberId: number
  ): Observable<MemberRetirementProductSubmissionAvailability> {
    const url = Endpoints.ipGetMemberRetirementProductSubmissionAvailability(memberId);
    return this.apiClient.getObj(MemberRetirementProductSubmissionAvailability, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetRetirementProductSubmissionAvailability(): Observable<MpRetirementProductSubmissionAvailability> {
    const url = Endpoints.mpGetRetirementProductSubmissionAvailability();
    return this.apiClient.getObj(MpRetirementProductSubmissionAvailability, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMemberSubmissionAttachment(
    req: CreateAttachmentRequest,
    submissionId: string
  ): Observable<SubmissionAttachment> {
    const url = Endpoints.ipCreateMemberSubmissionAttachment(submissionId);
    return this.apiClient.postObj(SubmissionAttachment, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateMemberSubmissionStatusWithComment(
    req: SubmissionStatusCommentRequest,
    url: string
  ): Observable<HttpResponse<any>> {
    return this.apiClient.putWithUntypedRes(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberSubmissionStatus(
    status: SubmissionSubStatusEnum,
    submissionId: string
  ): Observable<HttpResponse<any>> {
    let endpoint = '';
    switch (status) {
      case SubmissionSubStatusEnum.Processing:
        endpoint = Endpoints.ipSetMemberSubmissionToProcessing(submissionId);
        break;
      case SubmissionSubStatusEnum.Approved:
        endpoint = Endpoints.ipSetMemberSubmissionToApproved(submissionId);
        break;
    }
    return this.apiClient.putWithUntypedRes(endpoint, { id: submissionId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipAddMemberSubmissionComment(req: SubmissionCommentRequest): Observable<SubmissionComment[]> {
    const url = Endpoints.ipAddMemberSubmissionComment(req.submissionId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(SubmissionComment, res.body);
        return comments.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionApproversForSubmissionType(
    oDataQueryOptions: ODataQueryOptions,
    submissionTypeId?: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionApprovers();
    let params: string = '';
    if (submissionTypeId) {
      params = `submissionTypeId=${submissionTypeId}`;
    }
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, null, params)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipGetMemberSubmissionOwnersForSubmissionType(
    oDataQueryOptions: ODataQueryOptions,
    submissionTypeId?: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionOwners();
    let params: string = '';
    if (submissionTypeId) {
      params = `submissionTypeId=${submissionTypeId}`;
    }
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, null, params)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public getSubmissionRoleTemplates(): Observable<ODataResponse<SubmissionRoleTemplate>> {
    const url = Endpoints.getSubmissionRoleTemplates();
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient.getOdata<SubmissionRoleTemplate>(url, SubmissionRoleTemplate, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getSubmissionPermissionTypes(): Observable<SubmissionPermissionType[]> {
    const url = Endpoints.getSubmissionPermissionTypes();
    return this.apiClient.getArr<SubmissionPermissionType>(SubmissionPermissionType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAllSubmissionPermissions(): Observable<ODataResponse<SubmissionPermissionWithType>> {
    const url = Endpoints.getAllSubmissionPermissions();
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient
      .getOdata<SubmissionPermissionWithType>(url, SubmissionPermissionWithType, oDataQueryOptions)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipGetMemberSubmissionTypes(): Observable<ODataResponse<MemberSubmissionType>> {
    const url = Endpoints.ipGetMemberSubmissionTypes();
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient.getOdata<MemberSubmissionType>(url, MemberSubmissionType, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberSubmissionTypes(): Observable<ODataResponse<MemberSubmissionType>> {
    const url = Endpoints.getMemberSubmissionTypes();
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient.getOdata<MemberSubmissionType>(url, MemberSubmissionType, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberSubmissionOwner(req: UpdateSubmissionOwnerRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipUpdateMemberSubmissionOwner(req.id.toString(10));
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateTransferInTransferRecord(req: UpdateTransferInTransferRecordRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipUpdateTransferInTransferRecord(req.id.toString(10));
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberSubmissionApprover(req: UpdateSubmissionApproverRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipUpdateMemberSubmissionApprover(req.id.toString(10));
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetMemberSubmissionToReadyForApproval(req: UpdateSubmissionApproverRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipSetMemberSubmissionToReadyForApproval(req.id.toString(10));
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMemberSubmissionReminder(req: SubmissionReminderRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipCreateMemberSubmissionReminder(req.memberSubmissionId);
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteMemberSubmissionReminder(submissionId: string, reminderId: string): Observable<HttpResponse<any>> {
    const url = Endpoints.ipDeleteMemberSubmissionReminder(submissionId, reminderId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteVariableBenefitSubmission(submissionId: number): Observable<HttpResponse<any>> {
    const url = Endpoints.ipDeleteVariableBenefitSubmission(submissionId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteVariableBenefitConfigurationConsentWaiver(submissionId: number): Observable<HttpResponse<any>> {
    const url = Endpoints.ipDeleteVariableBenefitConfigurationConsentWaiver(submissionId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSaveVariableBenefitSubmission(
    req: CreateVariableBenefitConfigurationSubmissionRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipSaveVariableBenefitConfigurationSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEstimatedAnnualPaymentTotalForVbConfiguration(
    req: VbConfigurationEstimatedAnnualPaymentTotalRequest
  ): Observable<MemberVbAccountEstimatedAnnualPaymentTotal> {
    const url = Endpoints.ipGetEstimatedAnnualPaymentTotalForVbConfiguration();
    return this.apiClient.postObj(MemberVbAccountEstimatedAnnualPaymentTotal, url, req, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSubmitVariableBenefitSubmission(
    req: CreateVariableBenefitConfigurationSubmissionRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipSubmitVariableBenefitConfigurationSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberVariableBenefitConfigSubmission(
    req: UpdateVariableBenefitConfigurationRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipUpdateMemberVariableBenefitConfigSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSubmitAnnuitySetupSubmission(req: CreateAnnuitySetupSubmissionRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipSubmitAnnuitySetupSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMemberVariableBenefitPaymentChangeSubmission(
    req: CreateVariableBenefitPaymentChangeRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipCreateMemberVariableBenefitPaymentChangeSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateMemberVariableBenefitPaymentChangeSubmission(
    req: CreateVariableBenefitPaymentChangeRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.mpCreateMemberVariableBenefitPaymentChangeSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Employer Submission Methods

  public ipGetEmployerSubmissionById(id: string, queryOptions: ODataQueryOptions): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipGetEmployerSubmissionById(id);
    return this.apiClient.getOdataObj<IpEmployerSubmission>(url, IpEmployerSubmission, queryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionViewersForEmployerSubmissionType(
    submissionTypeId: EmployerSubmissionTypeEnum
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionViewersForEmployerSubmissionType();
    const oDataQueryOptions = new ODataQueryOptions();
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, null, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipCreateEmployerSubmissionAttachment(
    req: CreateAttachmentRequest,
    submissionId: string
  ): Observable<EmployerSubmissionAttachment> {
    const url = Endpoints.ipCreateEmployerSubmissionAttachment(submissionId);
    return this.apiClient.postObj(EmployerSubmissionAttachment, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipAddEmployerSubmissionComment(req: SubmissionCommentRequest): Observable<SubmissionComment[]> {
    const url = Endpoints.ipAddEmployerSubmissionComment(req.submissionId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(SubmissionComment, res.body);
        return comments.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerSubmissionStatusWithComment(
    req: SubmissionStatusCommentRequest,
    url: string
  ): Observable<IpEmployerSubmission> {
    return this.apiClient.putObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerSubmissionStatus(
    status: SubmissionSubStatusEnum,
    submissionId: string
  ): Observable<IpEmployerSubmission> {
    let endpoint = '';
    switch (status) {
      case SubmissionSubStatusEnum.Processing:
        endpoint = Endpoints.ipSetEmployerSubmissionToProcessing(submissionId);
        break;
      case SubmissionSubStatusEnum.Approved:
        endpoint = Endpoints.ipSetEmployerSubmissionToApproved(submissionId);
        break;
    }
    const req = new UpdateSubmissionStatusRequest(+submissionId);
    return this.apiClient.putObj(IpEmployerSubmission, endpoint, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionApproversForSubmissionType(
    submissionTypeId: EmployerSubmissionTypeEnum
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionApprovers();
    const oDataQueryOptions = new ODataQueryOptions();
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipGetEmployerSubmissionOwnersForSubmissionType(
    submissionTypeId: EmployerSubmissionTypeEnum
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionOwners();
    const oDataQueryOptions = new ODataQueryOptions();
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipUpdateEmployerSubmissionOwner(req: UpdateSubmissionOwnerRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipUpdateEmployerSubmissionOwner(req.id.toString(10));
    return this.apiClient.putObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerSubmissionApprover(req: UpdateSubmissionApproverRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipUpdateEmployerSubmissionApprover(req.id.toString(10));
    return this.apiClient.putObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetEmployerSubmissionToReadyForApproval(
    req: UpdateSubmissionApproverRequest
  ): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipSetEmployerSubmissionToReadyForApproval(req.id.toString(10));
    return this.apiClient.putObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateEmployerSubmissionReminder(req: EmployerSubmissionReminderRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipCreateEmployerSubmissionReminder(req.employerSubmissionId);
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteEmployerSubmissionReminder(submissionId: string, reminderId: string): Observable<HttpResponse<any>> {
    const url = Endpoints.ipDeleteEmployerSubmissionReminder(submissionId, reminderId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerSubmissionById(
    id: string,
    oDataQueryOptions: ODataQueryOptions
  ): Observable<EpEmployerSubmission> {
    const url = Endpoints.epGetEmployerSubmissionById(id);
    return this.apiClient.getOdataObj<EpEmployerSubmission>(url, EpEmployerSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerSubmissions(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<EpEmployerSubmission>> {
    const url = Endpoints.epGetEmployerSubmissions();
    return this.apiClient.getOdata<EpEmployerSubmission>(url, EpEmployerSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetApprovedSubmissionsForMember(memberId: number): Observable<MemberApprovedSubmission[]> {
    const url = Endpoints.ipGetApprovedSubmissionsForMember(memberId);
    return this.apiClient.getArr(MemberApprovedSubmission, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetMemberSubmissions(memberId: number): Observable<EmployerMemberSubmission[]> {
    const url = Endpoints.epGetMemberSubmissions(memberId);
    return this.apiClient.getArr(EmployerMemberSubmission, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
